import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts'
import {
  BookOpen, Clock, Target, AlertTriangle, ChevronDown,
  ChevronRight, Calendar, Flame, Brain, Gamepad2,
} from 'lucide-react'
import { api } from '../../lib/api'

// ─── Types ───────────────────────────────────────────────────

interface TrackerData {
  student: { id: number; name: string }
  focus: { currentGroup: string | null; focusThrough: string | null }
  upcomingTest: { name: string; dueAt: string; course: string } | null
  weekStats: {
    totalTimeSeconds: number
    sessionsPlayed: number
    wordsPracticed: number
    accuracy: number
  }
  sessions: SessionData[]
  dailyData: DailyPoint[]
  masteryByGroup: GroupMastery[]
  hardestWords: HardWord[]
  gameMix: GameMixEntry[]
}

interface SessionData {
  id: number
  gameId: string
  gameName: string
  startedAt: string
  durationSeconds: number
  questionsTotal: number
  questionsCorrect: number
  accuracy: number
  avgResponseMs: number | null
  xpEarned: number
  score: number | null
}

interface DailyPoint {
  date: string
  sessions: number
  timeSeconds: number
  accuracy: number | null
  words: number
}

interface GroupMastery {
  group: string
  total: number
  levels: Record<number, number>
  isCurrent: boolean
}

interface HardWord {
  word: string
  masteryLevel: number
  attempts: number
  accuracy: number
  streak: number
  lastSeen: string | null
  isLapsed: boolean
  listGroup: string
}

interface GameMixEntry {
  game: string
  timeSeconds: number
  pct: number
  sessions: number
  avgAccuracy: number
}

// ─── Helpers ─────────────────────────────────────────────────

function formatTime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function getTimeUntil(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now()
  if (diff < 0) return 'Past due'
  const hours = Math.floor(diff / (1000 * 60 * 60))
  if (hours < 24) return hours <= 1 ? 'In ~1 hour' : `In ${hours} hours`
  const days = Math.floor(hours / 24)
  return days === 1 ? 'Tomorrow' : `In ${days} days`
}

const MASTERY_COLORS: Record<number, string> = {
  0: '#374151', // gray-700 — New
  1: '#eab308', // yellow — Learning
  2: '#3b82f6', // blue — Familiar
  3: '#8b5cf6', // purple — Practiced
  4: '#22c55e', // green — Proficient
  5: '#10b981', // emerald — Mastered
}

const MASTERY_LABELS: Record<number, string> = {
  0: 'New', 1: 'Learning', 2: 'Familiar', 3: 'Practiced', 4: 'Proficient', 5: 'Mastered',
}

const GAME_COLORS = ['#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#22c55e']

// ─── Component ───────────────────────────────────────────────

export default function SpellingTracker() {
  // Student selector
  const { data: studentsData } = useQuery({
    queryKey: ['parent-students'],
    queryFn: async () => {
      const res = await api.get('/api/parent/students')
      return res.data.students as { id: number; name: string }[]
    },
  })
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null)
  const studentId = selectedStudentId || studentsData?.[0]?.id

  const { data, isLoading, error } = useQuery<TrackerData>({
    queryKey: ['spelling-tracker', studentId],
    queryFn: async () => {
      const res = await api.get(`/api/parent/students/${studentId}/spelling-tracker`)
      return res.data
    },
    enabled: !!studentId,
    staleTime: 2 * 60 * 1000,
  })

  const [expandedSession, setExpandedSession] = useState<number | null>(null)

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 bg-surface-card rounded-lg w-64" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-24 bg-surface-card rounded-xl" />)}
        </div>
        <div className="h-64 bg-surface-card rounded-xl" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-gray-400">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
          <p>Failed to load spelling tracker data</p>
        </div>
      </div>
    )
  }

  const { weekStats, upcomingTest, sessions, dailyData, masteryByGroup, hardestWords, gameMix, focus } = data

  return (
    <div className="space-y-6 pb-8">
      {/* ─── Header ─────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Spelling Tracker</h1>
            <p className="text-sm text-gray-400">
              {data.student.name}
              {focus.currentGroup && <span className="ml-2 text-amber-400">— Focus: Lists {focus.currentGroup}</span>}
            </p>
          </div>
        </div>

        {/* Student selector */}
        {studentsData && studentsData.length > 1 && (
          <select
            value={studentId || ''}
            onChange={e => setSelectedStudentId(Number(e.target.value))}
            className="bg-surface-card border border-surface-border rounded-lg px-3 py-2 text-sm"
          >
            {studentsData.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* ─── Test Alert ─────────────────────────── */}
      {upcomingTest && (
        <div className="relative overflow-hidden rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-surface-card to-surface-card p-4">
          <div className="absolute top-0 left-0 w-1 h-full bg-amber-500" />
          <div className="flex items-center justify-between flex-wrap gap-3 pl-3">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-300">{upcomingTest.name}</p>
                <p className="text-sm text-gray-400">{upcomingTest.course}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-amber-400">{getTimeUntil(upcomingTest.dueAt)}</p>
              <p className="text-xs text-gray-500">{formatDate(upcomingTest.dueAt)}</p>
            </div>
          </div>
        </div>
      )}

      {/* ─── Week Stats Cards ───────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          icon={Clock}
          label="Practice Time"
          value={formatTime(weekStats.totalTimeSeconds)}
          sub="this week"
          color="text-blue-400"
          bgColor="bg-blue-500/10"
        />
        <StatCard
          icon={Flame}
          label="Sessions"
          value={String(weekStats.sessionsPlayed)}
          sub="this week"
          color="text-orange-400"
          bgColor="bg-orange-500/10"
        />
        <StatCard
          icon={Brain}
          label="Words Practiced"
          value={String(weekStats.wordsPracticed)}
          sub="this week"
          color="text-purple-400"
          bgColor="bg-purple-500/10"
        />
        <StatCard
          icon={Target}
          label="Accuracy"
          value={`${weekStats.accuracy}%`}
          sub="this week"
          color={weekStats.accuracy >= 80 ? 'text-green-400' : weekStats.accuracy >= 60 ? 'text-yellow-400' : 'text-red-400'}
          bgColor={weekStats.accuracy >= 80 ? 'bg-green-500/10' : weekStats.accuracy >= 60 ? 'bg-yellow-500/10' : 'bg-red-500/10'}
        />
      </div>

      {/* ─── Main Grid ──────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ─── Practice Timeline (2 cols) ───────── */}
        <div className="lg:col-span-2 bg-surface-card rounded-xl p-5 border border-surface-border">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">Daily Practice</h2>
          {dailyData.some(d => d.sessions > 0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={dailyData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="timeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e2d3d" />
                <XAxis
                  dataKey="date"
                  tickFormatter={v => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  tick={{ fill: '#6b7280', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="time"
                  tick={{ fill: '#6b7280', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => `${Math.round(v / 60)}m`}
                />
                <YAxis
                  yAxisId="acc"
                  orientation="right"
                  domain={[0, 100]}
                  tick={{ fill: '#6b7280', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => `${v}%`}
                />
                <Tooltip content={<DailyTooltip />} />
                <Area
                  yAxisId="time"
                  type="monotone"
                  dataKey="timeSeconds"
                  stroke="#3b82f6"
                  fill="url(#timeGrad)"
                  strokeWidth={2}
                  name="Time"
                />
                <Area
                  yAxisId="acc"
                  type="monotone"
                  dataKey="accuracy"
                  stroke="#22c55e"
                  fill="none"
                  strokeWidth={2}
                  strokeDasharray="4 2"
                  name="Accuracy"
                  connectNulls
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-500 text-sm">
              No practice sessions yet this period
            </div>
          )}
        </div>

        {/* ─── Game Mix (1 col) ─────────────────── */}
        <div className="bg-surface-card rounded-xl p-5 border border-surface-border">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">Game Mix</h2>
          {gameMix.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie
                    data={gameMix}
                    dataKey="timeSeconds"
                    nameKey="game"
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={60}
                    paddingAngle={2}
                  >
                    {gameMix.map((_, i) => (
                      <Cell key={i} fill={GAME_COLORS[i % GAME_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null
                      const d = payload[0].payload as GameMixEntry
                      return (
                        <div className="bg-surface-base border border-gray-600 rounded-lg p-2 text-xs shadow-lg">
                          <p className="font-medium">{d.game}</p>
                          <p className="text-gray-400">{formatTime(d.timeSeconds)} · {d.sessions} sessions · {d.avgAccuracy}% acc</p>
                        </div>
                      )
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {gameMix.map((g, i) => (
                  <div key={g.game} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: GAME_COLORS[i % GAME_COLORS.length] }} />
                      <span className="text-gray-300">{g.game}</span>
                    </div>
                    <span className="text-gray-500">{g.pct}%</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-500 text-sm">
              No game data yet
            </div>
          )}
        </div>
      </div>

      {/* ─── Mastery by List Group ──────────────── */}
      {masteryByGroup.length > 0 && (
        <div className="bg-surface-card rounded-xl p-5 border border-surface-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Mastery by List Group</h2>
            <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
              {[0, 1, 2, 3, 4, 5].map(level => (
                <span key={level} className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: MASTERY_COLORS[level] }} />
                  {MASTERY_LABELS[level]}
                </span>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {masteryByGroup.map(g => {
              const total = g.total || 1
              return (
                <div key={g.group} className="flex items-center gap-3">
                  <div className={`w-16 text-right text-xs font-mono ${g.isCurrent ? 'text-amber-400 font-bold' : 'text-gray-400'}`}>
                    {g.group}
                    {g.isCurrent && <span className="ml-1">★</span>}
                  </div>
                  <div className="flex-1 flex h-5 rounded-md overflow-hidden bg-gray-800/50">
                    {[5, 4, 3, 2, 1, 0].map(level => {
                      const count = g.levels[level] || 0
                      if (count === 0) return null
                      const pct = (count / total) * 100
                      return (
                        <div
                          key={level}
                          className="h-full transition-all duration-300"
                          style={{ width: `${pct}%`, backgroundColor: MASTERY_COLORS[level] }}
                          title={`${MASTERY_LABELS[level]}: ${count} words`}
                        />
                      )
                    })}
                  </div>
                  <div className="w-8 text-right text-xs text-gray-500">{g.total}</div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ─── Bottom Grid ────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* ─── Recent Sessions ──────────────────── */}
        <div className="bg-surface-card rounded-xl p-5 border border-surface-border">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">Recent Sessions</h2>
          {sessions.length > 0 ? (
            <div className="space-y-1">
              {sessions.slice(0, 12).map(s => (
                <button
                  key={s.id}
                  onClick={() => setExpandedSession(expandedSession === s.id ? null : s.id)}
                  className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-surface-hover transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      {expandedSession === s.id ? (
                        <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
                      )}
                      <Gamepad2 className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-200">{s.gameName}</span>
                    </div>
                    <span className="text-xs text-gray-500">{formatDateTime(s.startedAt)}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 ml-9 text-xs text-gray-400">
                    <span>{formatTime(s.durationSeconds)}</span>
                    <span>{s.questionsTotal} words</span>
                    <span className={s.accuracy >= 80 ? 'text-green-400' : s.accuracy >= 60 ? 'text-yellow-400' : 'text-red-400'}>
                      {s.accuracy}% acc
                    </span>
                    {s.avgResponseMs && <span>{(s.avgResponseMs / 1000).toFixed(1)}s avg</span>}
                    {s.xpEarned > 0 && <span className="text-amber-400">+{s.xpEarned} XP</span>}
                  </div>
                  {expandedSession === s.id && (
                    <SessionDetail sessionId={s.id} studentId={data.student.id} />
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 text-sm">No sessions recorded yet</div>
          )}
        </div>

        {/* ─── Hardest Words ────────────────────── */}
        <div className="bg-surface-card rounded-xl p-5 border border-surface-border">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
            Hardest Words
          </h2>
          {hardestWords.length > 0 ? (
            <div className="space-y-1">
              {hardestWords.map(w => (
                <div key={w.word} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-surface-hover transition-colors">
                  <div className="flex items-center gap-3">
                    <MasteryDot level={w.masteryLevel} isLapsed={w.isLapsed} />
                    <div>
                      <span className="text-sm font-medium text-gray-200">{w.word}</span>
                      <span className="ml-2 text-xs text-gray-600">L{w.listGroup}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span>{w.attempts} tries</span>
                    <span className={w.accuracy >= 70 ? 'text-green-400' : w.accuracy >= 40 ? 'text-yellow-400' : 'text-red-400'}>
                      {w.accuracy}%
                    </span>
                    <span>🔥 {w.streak}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 text-sm">
              No struggling words yet — start practicing!
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, color, bgColor }: {
  icon: React.ComponentType<{ className?: string }>
  label: string; value: string; sub: string; color: string; bgColor: string
}) {
  return (
    <div className="bg-surface-card rounded-xl p-4 border border-surface-border">
      <div className="flex items-center gap-2.5 mb-2">
        <div className={`w-8 h-8 rounded-lg ${bgColor} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
    </div>
  )
}

function MasteryDot({ level, isLapsed }: { level: number; isLapsed: boolean }) {
  const color = MASTERY_COLORS[level] || MASTERY_COLORS[0]
  return (
    <div className="relative">
      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
      {isLapsed && (
        <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full border border-surface-card" />
      )}
    </div>
  )
}

function DailyTooltip({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) {
  if (!active || !payload || !label) return null
  const d = payload[0]?.payload as DailyPoint | undefined
  if (!d || d.sessions === 0) return null

  return (
    <div className="bg-surface-base border border-gray-600 rounded-lg p-3 shadow-xl text-xs">
      <p className="font-medium text-gray-200 mb-1">{formatDate(label)}</p>
      <p className="text-gray-400">{d.sessions} session{d.sessions !== 1 ? 's' : ''} · {d.words} words</p>
      <p className="text-blue-400">Time: {formatTime(d.timeSeconds)}</p>
      {d.accuracy !== null && (
        <p className={d.accuracy >= 80 ? 'text-green-400' : 'text-yellow-400'}>Accuracy: {d.accuracy}%</p>
      )}
    </div>
  )
}

function SessionDetail({ sessionId, studentId }: { sessionId: number; studentId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['session-questions', sessionId],
    queryFn: async () => {
      const res = await api.get(`/api/parent/students/${studentId}/game-sessions/${sessionId}/questions`)
      return res.data.questions as { question: string; student_answer: string; is_correct: boolean; response_time_ms: number }[]
    },
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading) return <div className="ml-9 mt-2 text-xs text-gray-500">Loading...</div>
  if (!data || data.length === 0) return <div className="ml-9 mt-2 text-xs text-gray-500">No detailed data available</div>

  return (
    <div className="ml-9 mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1" onClick={e => e.stopPropagation()}>
      {data.map((q, i) => (
        <div
          key={i}
          className={`text-xs px-2 py-1 rounded ${q.is_correct ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}
        >
          <span className="font-medium">{q.question?.replace(/^Spell:\s*/i, '') || q.student_answer}</span>
          {!q.is_correct && q.student_answer && (
            <span className="text-gray-500 ml-1">→ {q.student_answer}</span>
          )}
          {q.response_time_ms > 0 && (
            <span className="text-gray-600 ml-1">{(q.response_time_ms / 1000).toFixed(1)}s</span>
          )}
        </div>
      ))}
    </div>
  )
}
